<template>
  <div class="qa-wrap">
    <!-- Repetition date tabs (repeating questions) -->
    <div v-if="hasRepetition" class="qa-dates">
      <span class="qa-dates-label">{{ $t('surveyBuilder.repeatSubmissions') || 'Repeat submissions' }}:</span>
      <button
        v-for="(d, i) in dateTabs"
        :key="i"
        class="qa-date"
        :class="{ active: d.key === activeDateKey, empty: !d.hasResponse }"
        @click="selectedDateKey = d.key"
      >
        {{ d.label }}
        <span v-if="!d.hasResponse" class="qa-date-status">· {{ $t('surveyBuilder.notAnswered') || 'Not answered' }}</span>
      </button>
    </div>

    <!-- Separate response per farm: one row per farm (Farm | Date | Response) -->
    <div v-if="isSeparateFarm && farmSections.length">
      <table class="qa-farm-table">
        <thead>
          <tr>
            <th style="width: 180px">{{ $t('surveyBuilder.farmColumn') }}</th>
            <th style="width: 220px">{{ $t('surveyBuilder.submittedDate') || 'Submitted Date' }}</th>
            <th>{{ $t('esgPlatform.responses') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(section, i) in farmSections" :key="i">
            <td class="qa-top"><span class="qa-farm-chip">{{ section.farmName }}</span></td>
            <td class="qa-top qa-date">{{ formatDate(section.submittedOn) }}</td>
            <td class="qa-top">
              <answer-block :question="synth(section.response)"></answer-block>
              <div v-if="section.rejectionReason" class="qa-reject">
                {{ $t('surveyBuilder.rejectionReason') }}: {{ section.rejectionReason }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Single / repeating (non-farm) answer -->
    <div v-else>
      <answer-block :question="synth(activeResponse ? activeResponse.response : null)"></answer-block>
      <div v-if="activeResponse && activeResponse.rejectionReason" class="qa-reject">
        {{ $t('surveyBuilder.rejectionReason') }}: {{ activeResponse.rejectionReason }}
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import ResponseAnswerBlock from "./ResponseAnswerBlock.vue";

const dateKeyOf = (d) => (d ? moment(d).format("YYYY-MM-DD") : null);

export default {
  name: "QuestionAnswer",
  components: { "answer-block": ResponseAnswerBlock },
  props: {
    question: { type: Object, required: true },
  },
  data() {
    return { selectedDateKey: null };
  },
  computed: {
    responses() {
      return this.question.responses || [];
    },
    hasRepetition() {
      return (this.question.repetitionDates || []).length > 0;
    },
    isSeparateFarm() {
      return (
        this.question.isSeparateFarm ||
        this.responses.some((r) => (r.farmNames && r.farmNames.length) || (r.farmIds && r.farmIds.length))
      );
    },
    // One tab per scheduled repetition date, marking which have a response.
    dateTabs() {
      return (this.question.repetitionDates || []).map((raw) => {
        const key = dateKeyOf(raw);
        return {
          key,
          label: raw ? moment(raw).format("DD/MM/YY") : "—",
          hasResponse: this.responses.some((r) => dateKeyOf(r.repetitionDate) === key),
        };
      });
    },
    activeDateKey() {
      if (this.selectedDateKey) return this.selectedDateKey;
      // default: the latest date that actually has a response, else the last tab
      const answered = this.dateTabs.filter((d) => d.hasResponse);
      if (answered.length) return answered[answered.length - 1].key;
      return this.dateTabs.length ? this.dateTabs[this.dateTabs.length - 1].key : null;
    },
    // Responses scoped to the active date (or all responses when not repeating).
    activeResponses() {
      if (!this.hasRepetition) return this.responses;
      return this.responses.filter((r) => dateKeyOf(r.repetitionDate) === this.activeDateKey);
    },
    activeResponse() {
      const list = this.activeResponses;
      return list.length ? list[list.length - 1] : null;
    },
    // One row per farm (separate-response-per-farm questions).
    farmSections() {
      return this.activeResponses.map((r) => ({
        farmName: r.farmNames && r.farmNames.length ? r.farmNames.join(", ") : "-",
        response: r.response,
        submittedOn: r.submittedOn,
        rejectionReason: r.rejectionReason,
      }));
    },
  },
  methods: {
    // Minimal question object for the shared read-only answer renderer.
    synth(response) {
      return {
        questionType: this.question.questionType,
        options: this.question.options || [],
        getDataQuestions: this.question.getDataQuestions || [],
        dimitraModule: this.question.dimitraModule || null,
        response,
        responses: [],
      };
    },
    formatDate(d) {
      return d ? moment(d).format("DD MMMM YYYY, hh:mm A") : "—";
    },
  },
};
</script>

<style scoped>
.qa-dates {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.qa-dates-label {
  font-size: 12px;
  color: #787878;
  margin-right: 2px;
}
.qa-date {
  border: 1px solid #e3e3e3;
  background: #fff;
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 12px;
  color: #444;
  cursor: pointer;
}
.qa-date:hover {
  border-color: #0ebf67;
}
.qa-date.active {
  background: #e5f8f1;
  border-color: #0ebf67;
  color: #117443;
  font-weight: 600;
}
.qa-date.empty {
  color: #979797;
}
.qa-date-status {
  font-style: italic;
}
.qa-farm-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #eceff3;
  border-radius: 8px;
  overflow: hidden;
}
.qa-farm-table thead th {
  text-align: left;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 500;
  color: #ffffff;
  background: #00bd73;
  border-bottom: 1px solid #eceff3;
}
.qa-farm-table tbody td {
  padding: 12px 16px;
  font-size: 14px;
  color: #1e1e1e;
  border-bottom: 1px solid #f2f4f6;
}
.qa-farm-table tbody tr:last-child td {
  border-bottom: 0;
}
.qa-top {
  vertical-align: top;
}
.qa-date {
  color: #5c5959;
  white-space: nowrap;
}
.qa-farm-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: #117443;
  background: #e5f8f1;
  border: 1px solid #0ebf67;
}
.qa-reject {
  margin-top: 8px;
  color: #f03737;
  font-size: 12px;
}
</style>
